import { useCallback, useEffect } from 'react';
import {
  useStoreActions,
  useStoreRehydrated,
  useStoreState,
} from '@discover-data/stores/restaurant/hooks';
import useSyncInputs, { type UseSyncInputsProps } from './useSyncInputs';
import useSyncRegionId from './useSyncRegionId';

type useRestaurantsProps = UseSyncInputsProps & {
  regionId: number;
};

export default function useRestaurants({ regionId, ...inputs }: useRestaurantsProps) {
  useSyncRegionId(regionId);
  useSyncInputs(inputs);

  // handle starting and stopping the store
  const setMode = useStoreActions((state) => state.setMode);
  const enable = useStoreActions((state) => state.enable);
  const allAvailabilityUnsubAll = useStoreActions(
    (state) => state.firebaseAllAvailability.firebase.unsubAll
  );
  const regularTableSyncUnsubAll = useStoreActions(
    (state) => state.firebaseRegularTableSync.firebase.unsubAll
  );
  const abortAllIds = useStoreActions((state) => state.allIds.abort);
  const abortById = useStoreActions((state) => state.byId.abort);
  const abortFirebaseAllAvailability = useStoreActions(
    (state) => state.abortFirebaseAllAvailability
  );
  const abortAllAvailabilitySearch = useStoreActions((state) => state.abortAllAvailabilitySearch);
  const abortFirebaseRegularTableSync = useStoreActions(
    (state) => state.abortFirebaseRegularTableSync
  );
  const rehydrated = useStoreRehydrated();

  const onMount = useCallback(() => {
    setMode('list');
    enable();
  }, [setMode, enable]);

  const onUnmount = useCallback(() => {
    // stop all ongoing fetches
    abortAllIds();
    abortById();
    abortFirebaseAllAvailability();
    abortAllAvailabilitySearch();
    abortFirebaseRegularTableSync();

    // unsub from all Firebase subscriptions
    allAvailabilityUnsubAll();
    regularTableSyncUnsubAll();
  }, []);

  useEffect(() => {
    if (rehydrated) {
      onMount();
    }

    return onUnmount;
  }, [onMount, onUnmount, rehydrated]);

  // get the list of restaurants to show and the loading state
  const restaurants = useStoreState((state) => state.restaurantsToShow);
  const loading = useStoreState((state) => state.loading);

  return {
    restaurants,
    loading,
  };
}
